import { useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

export interface TabsControllerProps<T> {
	/** Tab key name in url */
	keyName: string
	/** Default tab value */
	defaultValue: T
}

/**
 * Used to get a tab from url query string and change the url to match current tab
 * @return tab - current tab taken from url, changeTab - function to change url to match current tab
 */
export const useTabsController = <T extends string>({
	keyName,
	defaultValue,
}: TabsControllerProps<T>) => {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()

	const currentTab = (searchParams.get(keyName) as T) || defaultValue

	const changeTab = useCallback(
		(value: T) => {
			const newSearchParams = new URLSearchParams(searchParams)

			if (value === defaultValue) {
				newSearchParams.delete(keyName)
			} else {
				newSearchParams.set(keyName, value)
			}

			const searchString = newSearchParams.toString()
			const newUrl = `${location.pathname}${searchString ? `?${searchString}` : ''}`
			navigate(newUrl, {
				replace: true,
			})
		},
		[defaultValue, keyName, navigate, searchParams],
	)

	return {
		tab: currentTab,
		changeTab,
	}
}
